import { ZoomIn, ZoomOut, Maximize2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OrgChartControlsProps {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  disabled?: boolean;
}

/**
 * Control panel for org chart (zoom, pan, refresh)
 * Fixed position at bottom-right of chart
 */
export function OrgChartControls({
  zoom,
  onZoomIn,
  onZoomOut,
  onResetView,
  onRefresh,
  isRefreshing = false,
  disabled = false,
}: OrgChartControlsProps) {
  const zoomPercent = Math.round(zoom * 100);
  const canZoomIn = zoom < 2;
  const canZoomOut = zoom > 0.5;

  return (
    <TooltipProvider>
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 bg-background border rounded-lg shadow-lg p-2 z-50">
        {/* Zoom In */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onZoomIn}
              disabled={disabled || !canZoomIn}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Zoom In</p>
          </TooltipContent>
        </Tooltip>

        {/* Zoom Level Display */}
        <div className="flex items-center justify-center text-xs text-muted-foreground font-medium px-2">
          {zoomPercent}%
        </div>

        {/* Zoom Out */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onZoomOut}
              disabled={disabled || !canZoomOut}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Zoom Out</p>
          </TooltipContent>
        </Tooltip>

        <div className="h-px bg-border my-1" />

        {/* Fit to Screen */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onResetView}
              disabled={disabled}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Fit to Screen</p>
          </TooltipContent>
        </Tooltip>

        {/* Refresh */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={disabled || isRefreshing}
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Refresh Org Chart</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
